/**
 * Service for Reports API
 * Manages abuse reports for advertisements and websites
 */

import { getUser } from './authService';
import { enTetesAuth } from './enTetesAuth';

const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/reports';

/**
 * Report reasons
 */
export const REPORT_REASONS = {
    adult_content: {
        fr: 'Contenu pour adultes / Pornographie',
        en: 'Adult content / Pornography',
        nl: 'Inhoud voor volwassenen / Pornografie'
    },
    spam: {
        fr: 'Spam ou contenu trompeur',
        en: 'Spam or misleading content',
        nl: 'Spam of misleidende inhoud'
    },
    harassment: {
        fr: 'Harcèlement ou intimidation',
        en: 'Harassment or bullying',
        nl: 'Intimidatie of pesten'
    },
    violence: {
        fr: 'Violence ou contenu choquant',
        en: 'Violence or shocking content',
        nl: 'Geweld of schokkende inhoud'
    },
    illegal: {
        fr: 'Activité illégale',
        en: 'Illegal activity',
        nl: 'Illegale activiteit'
    },
    other: {
        fr: 'Autre raison',
        en: 'Other reason',
        nl: 'Andere reden'
    }
};

/**
 * Create a new abuse report
 * @param {Object} reportData - Report data
 * @param {string} reportData.contentType - Type of content ('advertisement' or 'website')
 * @param {number} reportData.contentId - ID of the content
 * @param {string} reportData.contentUuid - UUID of the content (optional)
 * @param {string} reportData.reason - Reason for the report
 * @param {string} reportData.description - Description (optional)
 * @param {string} reportData.reporterEmail - Reporter email (optional, for non-logged users)
 * @returns {Promise<Object>} Result
 */
export const createReport = async (reportData) => {
    try {
        const user = getUser();

        const body = {
            contentType: reportData.contentType,
            contentId: reportData.contentId,
            reason: reportData.reason,
            description: reportData.description || ''
        };

        if (reportData.contentUuid) {
            body.contentUuid = reportData.contentUuid;
        }

        // Add reporter info
        if (user?.id) {
            body.reporterPeopleId = user.id;
        } else if (reportData.reporterEmail) {
            body.reporterEmail = reportData.reporterEmail;
        }

        const response = await fetch(API_BASE_URL, {
            method: 'POST',
            headers: enTetesAuth(),
            credentials: 'include',
            body: JSON.stringify(body)
        });

        const text = await response.text();
        if (!text || text.trim() === '') {
            return {
                success: false,
                message: 'Service non disponible. Veuillez réessayer plus tard.'
            };
        }

        const result = JSON.parse(text);

        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error creating report:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Get report reasons for a specific language
 * @param {string} language - Language code (fr, en, nl)
 * @returns {Array} Array of { value, label } objects
 */
export const getReportReasonsForLanguage = (language = 'fr') => {
    const lang = language.substring(0, 2);
    return Object.entries(REPORT_REASONS).map(([value, labels]) => ({
        value,
        label: labels[lang] || labels.fr
    }));
};

export default {
    createReport,
    getReportReasonsForLanguage,
    REPORT_REASONS
};
